import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Edit, Trash2, Filter, Download, AlertTriangle, RefreshCw } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchCostEntries,
  deleteCostEntry,
  selectCostEntries,
  selectCostsLoading,
  selectCostsError,
  setFilters,
  selectCostFilters,
  clearError,
} from '@/store/slices/costsSlice';
import { LoadingSpinner, ConfirmModal } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassSelect,
} from '@/design-system';
import { CostCategory, UserRole } from '@/types';
import type { OperationalCostEntry } from '@/types';
import CostEntryModal from './CostEntryModal';

interface CostEntryTableProps {
  programId: string;
  programName: string;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getCategoryLabel = (category: CostCategory): string => {
  const labels: Record<CostCategory, string> = {
    [CostCategory.LABOR]: 'Labor',
    [CostCategory.SOFTWARE_LICENSES]: 'Software Licenses',
    [CostCategory.INFRASTRUCTURE]: 'Infrastructure',
    [CostCategory.TRAINING]: 'Training',
    [CostCategory.TRAVEL]: 'Travel',
    [CostCategory.CONTRACTOR_SERVICES]: 'Contractor Services',
    [CostCategory.OTHER]: 'Other',
  };
  return labels[category] || category;
};

const getCategoryColor = (category: CostCategory): string => {
  const colors: Record<CostCategory, string> = {
    [CostCategory.LABOR]: 'info',
    [CostCategory.SOFTWARE_LICENSES]: 'violet',
    [CostCategory.INFRASTRUCTURE]: 'cyan',
    [CostCategory.TRAINING]: 'emerald',
    [CostCategory.TRAVEL]: 'amber',
    [CostCategory.CONTRACTOR_SERVICES]: 'rose',
    [CostCategory.OTHER]: 'default',
  };
  return colors[category] || 'default';
};

const CostEntryTable: React.FC<CostEntryTableProps> = ({
  programId,
  programName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const costEntries = useSelector(selectCostEntries);
  const loading = useSelector(selectCostsLoading);
  const error = useSelector(selectCostsError);
  const filters = useSelector(selectCostFilters);
  const { user } = useSelector((state: RootState) => state.auth);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<OperationalCostEntry | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Role-based access
  const canEdit = user?.role === UserRole.ADMIN || user?.role === UserRole.FINANCE || user?.role === UserRole.PROGRAM_LEAD;

  useEffect(() => {
    if (programId) {
      const params: any = {};
      if (filters.fiscalYear) params.fiscalYear = filters.fiscalYear;
      if (filters.quarter) params.quarter = filters.quarter;
      if (filters.category) params.category = filters.category;
      dispatch(fetchCostEntries({ programId, params }));
    }
  }, [dispatch, programId, filters]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  const handleDelete = async () => {
    if (!selectedEntry) return;
    const result = await dispatch(deleteCostEntry(selectedEntry.id));
    if (deleteCostEntry.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedEntry(null);
    }
  };

  const handleExportCSV = () => {
    const csvRows = [
      ['Category', 'Description', 'Amount', 'Fiscal Year', 'Quarter', 'Month', 'Entered By', 'Date Entered'],
    ];

    costEntries.forEach((entry) => {
      csvRows.push([
        getCategoryLabel(entry.category),
        entry.description,
        entry.amount.toString(),
        entry.fiscalYear.toString(),
        `Q${entry.quarter}`,
        entry.month?.toString() || '',
        entry.enteredBy ? `${entry.enteredBy.firstName} ${entry.enteredBy.lastName}` : '',
        formatDate(entry.createdAt),
      ]);
    });

    const csvContent = csvRows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cost-entries-${programName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRefresh = () => {
    const params: any = {};
    if (filters.fiscalYear) params.fiscalYear = filters.fiscalYear;
    if (filters.quarter) params.quarter = filters.quarter;
    if (filters.category) params.category = filters.category;
    dispatch(fetchCostEntries({ programId, params }));
  };

  const currentYear = new Date().getFullYear();
  const fiscalYearOptions = [
    { value: '', label: 'All Years' },
    ...Array.from({ length: 5 }, (_, i) => ({
      value: (currentYear - 2 + i).toString(),
      label: `FY${currentYear - 2 + i}`,
    })),
  ];

  const quarterOptions = [
    { value: '', label: 'All Quarters' },
    { value: '1', label: 'Q1' },
    { value: '2', label: 'Q2' },
    { value: '3', label: 'Q3' },
    { value: '4', label: 'Q4' },
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...Object.values(CostCategory).map((cat) => ({
      value: cat,
      label: getCategoryLabel(cat),
    })),
  ];

  // Calculate totals
  const totalAmount = costEntries.reduce((sum, entry) => sum + entry.amount, 0);

  return (
    <div className="space-y-4">
      {/* Error Display */}
      {error && (
        <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-xl">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-status-error" />
            <p className="text-status-error text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text)]">Cost Entries</h3>
          <p className="text-sm text-[var(--muted)]">
            {costEntries.length} entries | Total: {formatCurrency(totalAmount)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            leftIcon={<RefreshCw className={`w-4 h-4 ${loading.entries ? 'animate-spin' : ''}`} />}
          >
            Refresh
          </GlassButton>
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<Filter className="w-4 h-4" />}
          >
            Filters
          </GlassButton>
          <GlassButton
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            disabled={costEntries.length === 0}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Export
          </GlassButton>
          {canEdit && (
            <GlassButton
              variant="primary"
              size="sm"
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Add Entry
            </GlassButton>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <GlassCard className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-2">
                Fiscal Year
              </label>
              <GlassSelect
                value={filters.fiscalYear?.toString() || ''}
                onChange={(value) =>
                  dispatch(setFilters({ fiscalYear: value ? parseInt(value) : null }))
                }
                options={fiscalYearOptions}
                fullWidth
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-2">
                Quarter
              </label>
              <GlassSelect
                value={filters.quarter?.toString() || ''}
                onChange={(value) =>
                  dispatch(setFilters({ quarter: value ? parseInt(value) : null }))
                }
                options={quarterOptions}
                fullWidth
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-2">
                Category
              </label>
              <GlassSelect
                value={filters.category || ''}
                onChange={(value) =>
                  dispatch(setFilters({ category: (value as CostCategory) || null }))
                }
                options={categoryOptions}
                fullWidth
              />
            </div>
          </div>
        </GlassCard>
      )}

      {/* Table */}
      <GlassCard padding="none">
        {loading.entries ? (
          <div className="flex items-center justify-center h-48">
            <LoadingSpinner size="lg" />
          </div>
        ) : costEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center">
            <p className="text-[var(--muted)] mb-2">No cost entries found</p>
            {canEdit && (
              <GlassButton
                variant="primary"
                size="sm"
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Add First Entry
              </GlassButton>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[var(--muted)] uppercase tracking-wide">
                    Category
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[var(--muted)] uppercase tracking-wide">
                    Description
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-[var(--muted)] uppercase tracking-wide">
                    Amount
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-[var(--muted)] uppercase tracking-wide">
                    Period
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[var(--muted)] uppercase tracking-wide">
                    Entered By
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-[var(--muted)] uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {costEntries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-[var(--border)] last:border-0 hover:bg-white/50 dark:hover:bg-white/5 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <GlassBadge variant={getCategoryColor(entry.category) as any} size="sm">
                        {getCategoryLabel(entry.category)}
                      </GlassBadge>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-[var(--text)] max-w-xs truncate">{entry.description}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-semibold text-[var(--text)]">
                        {formatCurrency(entry.amount)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-sm text-[var(--text)]">
                        FY{entry.fiscalYear} Q{entry.quarter}
                        {entry.month && ` M${entry.month}`}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-[var(--muted)]">
                        {entry.enteredBy
                          ? `${entry.enteredBy.firstName} ${entry.enteredBy.lastName}`
                          : 'Unknown'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {canEdit && (
                        <div className="flex items-center justify-end gap-1">
                          <GlassButton
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedEntry(entry);
                              setIsEditModalOpen(true);
                            }}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </GlassButton>
                          <GlassButton
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedEntry(entry);
                              setIsDeleteModalOpen(true);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-status-error" />
                          </GlassButton>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[var(--surface-hover)]">
                  <td colSpan={2} className="py-3 px-4 text-sm font-semibold text-[var(--text)]">
                    Total
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-bold text-accent-violet">
                    {formatCurrency(totalAmount)}
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Create Modal */}
      <CostEntryModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        programId={programId}
        entry={null}
      />

      {/* Edit Modal */}
      <CostEntryModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedEntry(null);
        }}
        programId={programId}
        entry={selectedEntry}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedEntry(null);
        }}
        onConfirm={handleDelete}
        title="Delete Cost Entry"
        message={`Are you sure you want to delete this cost entry "${selectedEntry?.description}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={loading.deleting}
      />
    </div>
  );
};

export default CostEntryTable;
